import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { ProgressBar } from "@/components/ProgressBar";
import { classAccent } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Flame, Award } from "lucide-react";

export default function StudentHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [elig, setElig] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, s, e] = await Promise.all([
        apiGet("/classes"),
        apiGet("/user/stats"),
        apiGet("/me/mentor-eligibility"),
      ]);
      setClasses(c);
      setStats(s);
      setElig(e.classes || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestMentor = async (classId) => {
    try {
      await apiPost("/me/mentor-requests", { class_id: classId });
      toast.success("Mentor request sent for approval!");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const nav = [{ to: "/home", label: "My Classes" }];

  return (
    <AppShell nav={nav}>
      <div className="animate-rise">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {user?.organization_name || "Your journey"}
            </p>
            <h1 className="text-4xl font-extrabold">Hey, {user?.name?.split(" ")[0]} 👋</h1>
          </div>
          {stats && (
            <div className="flex gap-3">
              <div className="rounded-2xl border-2 border-ink bg-white px-4 py-3 flex items-center gap-2 shadow-pop-sm" data-testid="streak-card">
                <Flame size={20} style={{ color: "#FF5722" }} />
                <div>
                  <div className="text-xl font-extrabold leading-none">{stats.streak?.current || 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">day streak</div>
                </div>
              </div>
              <div className="rounded-2xl border-2 border-ink bg-white px-4 py-3 flex items-center gap-2 shadow-pop-sm" data-testid="total-card">
                <Award size={20} style={{ color: "#31B8FF" }} />
                <div>
                  <div className="text-xl font-extrabold leading-none">{stats.streak?.total_days || 0}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">days done</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 rounded-3xl border-2 border-ink bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {classes.map((cls, i) => {
              const accent = classAccent(cls.slug, i, cls.color);
              const locked = !cls.unlocked;
              const eligItem = elig.find((e) => e.class_id === cls.id);
              return (
                <div key={cls.id} className="rounded-3xl border-2 border-ink overflow-hidden bg-white shadow-pop flex flex-col" data-testid={`class-card-${cls.slug}`}>
                  <div className="p-6" style={{ background: accent.bg, color: accent.fg }}>
                    <div className="flex items-center justify-between mb-10">
                      <div className="flex items-center gap-2 flex-wrap">
                        {cls.theme && (
                          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink" style={{ background: accent.tag, color: "#0F1116" }} data-testid={`class-theme-${cls.slug}`}>
                            {cls.theme}
                          </span>
                        )}
                        <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border-2 border-ink" style={{ background: "rgba(255,255,255,0.22)", color: accent.fg }}>
                          {cls.day_count} days
                        </span>
                      </div>
                      {locked && <Lock size={18} />}
                      {cls.finished && <Award size={18} />}
                    </div>
                    <h3 className="text-2xl font-extrabold leading-tight">{cls.title}</h3>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="font-reading text-sm text-muted-foreground mb-4 line-clamp-3 flex-1">{cls.description}</p>
                    <div className="mb-4">
                      <div className="flex justify-between text-xs font-bold mb-1">
                        <span>{cls.completed_count}/{cls.day_count}</span>
                        <span>{cls.progress}%</span>
                      </div>
                      <ProgressBar value={cls.progress} color={accent.bg} />
                    </div>
                    <Button
                      disabled={locked}
                      onClick={() => navigate(`/class/${cls.slug}`)}
                      data-testid={`open-class-${cls.slug}`}
                      className="w-full h-11 rounded-full border-2 border-ink font-bold press disabled:opacity-50"
                      style={!locked ? { background: "#0F1116", color: "#FFFDF5" } : {}}
                    >
                      {locked ? "Finish previous class" : cls.finished ? "Review" : "Continue"}
                      {!locked && <ArrowRight size={16} className="ml-1" />}
                    </Button>
                    {eligItem?.finished && !eligItem?.is_mentor && (
                      <button
                        onClick={() => requestMentor(cls.id)}
                        disabled={eligItem.request_status === "pending"}
                        data-testid={`become-mentor-${cls.slug}`}
                        className="mt-2 text-xs font-bold underline text-foreground disabled:no-underline disabled:text-muted-foreground"
                      >
                        {eligItem.request_status === "pending"
                          ? "Mentor request pending…"
                          : "🎓 Become a mentor for this class"}
                      </button>
                    )}
                    {eligItem?.is_mentor && (
                      <div className="mt-2 text-xs font-bold text-center" style={{ color: "#31B8FF" }}>
                        ✓ You mentor this class
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
